/* eslint-disable multiline-ternary */
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { CONTENT_QUERY_STRING } from '../../consts'
import { useAlert } from '../../hooks'
import { contentSlice, userSlice } from '../../reducers'
import { ArticleBodyLoading } from '../article-view/article-view-loading'
import Body from '../article/body'
import Container from '../container'
import TranslateDialog from '../translate-dialog'
import VideoPlayer from '../video-player'
import Header from './header'
import { useStyles } from './style'

const DEFAULT_LANGUAGE = { label: 'English', translatedLabel: 'English', value: 'en' }

const ContentOnlyArticleView = () => {
  const content = useSelector(contentSlice.selectors.selectContent)
  const allLanguages = useSelector(contentSlice.selectors.selectAllLanguages)
  const language = useSelector(contentSlice.selectors.selectLanguage)
  const profile = useSelector(userSlice.selectors.selectProfile)

  const [showTranslateDialog, setShowTranslateDialog] = useState(false)

  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { enqueueMsg } = useAlert()

  // Use current language as the default for the dialog to show the correct selection
  const currentLanguage = language || DEFAULT_LANGUAGE
  const profileLanguage = profile?.defaultLanguage
  const storedLanguage = localStorage.getItem('defaultLanguage')
  // Only use stored/profile default if no translation is active, otherwise use current language
  const defaultLanguage = currentLanguage.value !== 'en' ? currentLanguage.value : (profileLanguage || storedLanguage)

  const [setDefaultLanguage] = useEvent([{
    eventName: 'setDefaultLanguage',
    onSuccess: (payload) => {
      dispatch(userSlice.actions.setProfile(payload))
    },
    onError: (err) => {
      console.error('Unable to update default language', err)
      enqueueMsg('Unable to update default language', 'error')
    }
  }])

  const handleSetDefaultLanguage = (newDefaultLanguage) => {
    const partial = {
      defaultLanguage: newDefaultLanguage,
      source: 'content-page'
    }
    if (!profileLanguage || profileLanguage !== newDefaultLanguage) {
      setDefaultLanguage.trigger({ partial })
    }
  }

  const setTranslationLanguageOnUrl = useCallback((lang) => {
    const { pathname, search } = window.location
    dispatch(contentSlice.actions.setIsTranslating(true))
    if (lang && lang !== 'en') {
      const query = new URLSearchParams(search)
      query.set(CONTENT_QUERY_STRING.TRANSLATION, lang)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    } else {
      const query = new URLSearchParams(search)
      query.delete(CONTENT_QUERY_STRING.TRANSLATION)
      const queryString = query.toString()
      const fullPath = queryString ? `${pathname}?${queryString}` : pathname
      navigate(fullPath, { replace: true })
    }
    if (lang && lang === 'en') {
      const query = new URLSearchParams(search)
      query.set(CONTENT_QUERY_STRING.TRANSLATION, lang)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    }
  }, [navigate, dispatch])

  const handleArticleTranslate = (lang) => {
    if (lang === currentLanguage.value) {
      console.warn('The selected language is already the current language. No translation needed.')
      return
    }
    setTranslationLanguageOnUrl(lang)
    dispatch(contentSlice.actions.setLanguage(lang))
  }

  const handleTranslateClick = () => setShowTranslateDialog(true)
  const handleDialogClose = () => setShowTranslateDialog(false)

  if (window.DD_RUM && typeof window.DD_RUM.addTiming === 'function') {
    window.DD_RUM.addTiming('article_view_loaded')
  } else {
    console.log('addTiming is not available')
  }

  if (!content) return null

  return (
    <>
      {showTranslateDialog && (
        <TranslateDialog
          key={currentLanguage.value}
          open
          heading='Translate the article'
          subheading='Please select one of the options below, or type the language you wish'
          onClose={handleDialogClose}
          language={currentLanguage}
          allLanguages={allLanguages}
          defaultLanguage={defaultLanguage}
          onTranslate={handleArticleTranslate}
          handleSetDefaultLanguage={handleSetDefaultLanguage}
        />
      )}
      <div id={content.locationId} className={classes.contentGrid}>
        <Box className={classes.content}>
          <Container
            maxContentWidth={668}
            inset={2}
            classes={{ content: classes.article }}
            className={classes.container}
          >
            <Header
              content={content}
              language={currentLanguage}
              onTranslateClick={handleTranslateClick}
            />
            <VideoPlayer url={content?.videoUrl} />
            <React.Suspense fallback={<ArticleBodyLoading />}>
              <Body
                body={content?.body}
                className={classes.body}
              />
            </React.Suspense>
          </Container>
        </Box>
      </div>
    </>
  )
}

export default ContentOnlyArticleView
